<script setup>
    // Common
    import i18n from '@/common/i18n';

    // Vue
    import { defineEmits, defineExpose, inject, ref } from 'vue';

    // Store
    import { useContactStore } from '@/stores/contact/contactStore';
    import { useFileDownloadStore } from '@/stores/common/fileDownloadStore';
    import { useFileUploadStore } from '@/stores/common/fileUploadStore';

    import fileUploadDownloadService from '@/commonProject/service/fileUploadDownloadService';

    import { useLanguageStore } from '@/stores/common/languageStore';

    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    // Utils
    import utils from '@/commonProject/helpers/utils.js';

    // 言語情報取得
    const languageStore = useLanguageStore();

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    // Storeクラスを読み込む。
    const contactStore = useContactStore();
    const fileDownloadStore = useFileDownloadStore();
    const fileUploadStore = useFileUploadStore();

    // 親画面から取得したAPIのドメイン部を管理する。
    const API_URL = ref('');

    // 連絡モーダルの表示・非表示を管理する。
    const contactDispFlg = ref('');

    // APIで取得した、本モーダル内で使用する連絡リストの構造。
    const contactListBody = ref([]);

    const selectedUploadFile = ref([]);

    const contactMessage = ref('');

    // 登録ボタンの表示制御
    const disabledControl = ref(false);
    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    // ----------------------------
    // 連絡
    // ----------------------------
    async function openModal(trg) {
        contactDispFlg.value = 'block';
        API_URL.value = trg;

        // 初期化する。
        reset();

        // 連絡一覧取得をコールする。
        await postContactListInfo();

        // モーダルのHTML要素を取得する。
        const elm1 = document.querySelector('#modalContactToCounselor .modalContents');

        // モーダルの縦スクロールを一番下に戻す。
        elm1.scrollTop = elm1.scrollHeight - elm1.clientHeight;
    }

    /**
     * 連絡一覧APIをコールし、画面に連絡のやり取りを表示する。
     */
    async function postContactListInfo() {
        // 初期化する。
        contactListBody.value = [];

        // 連絡一覧APIのリクエストボディを生成する。
        const requestBody = {
            Lang: languageStore.getLanguageValue,
        };

        // Storeクラスを経由して連絡一覧APIをコールする。
        const response = await contactStore.postContactList(API_URL.value, requestBody);
        if (response.success) {
            // 画面表示用配列に格納。
            contactListBody.value = JSON.parse(JSON.stringify(response.responseBody.List));

            // 連絡のデータが存在するか判定する。
            if (!utils.isNullEmptyArray(contactListBody.value)) {
                // 全連絡データを検索する。
                for (const contact of contactListBody.value) {
                    // 未読の連絡データが存在するか確認する。
                    if (!utils.isNull(contact.ContactReaded) && !contact.ContactReaded) {
                        // 連絡既読登録APIのリクエストボディを生成する。
                        const requestBody2 = {
                            ContactMsgPk: contact.ContactMsgPk,
                            ContactDeleted: contact.ContactDeleted,
                        };

                        // 連絡既読登録APIのリクエストボディを生成する。
                        const response2 = await contactStore.postContactRead(API_URL.value, requestBody2);

                        if (!response2.success) {
                            messageDialog('error', response2.errorMessage);
                            if (response2.redirect) {
                                location.href = response2.redirectUrl;
                            }
                            return;
                        }
                    }
                }
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    } // #postContactListInfo

    // ----------------------------
    // 親画面で実装必須関数
    // ----------------------------
    const emit = defineEmits(['contactModal:cancel']);
    function cancel() {
        // 親画面のキャンセル処理を呼び出す
        emit('contactModal:cancel');
    }

    // ----------------------------
    // 親画面から利用可能関数
    // ----------------------------
    function testInfoSet() {}
    defineExpose({
        testInfoSet,
        openModal,
    });

    async function fileSelect(e) {
        const files = e.target.files || e.dataTransfer.files;
        const uploadTargetFile = {
            name: files[0].name,
            type: files[0].type,
            data: files[0],
        };

        selectedUploadFile.value.push(uploadTargetFile);

        document.getElementById('file').value = '';
    }

    async function removeUploadTargetFile(num) {
        selectedUploadFile.value.splice(num, 1);
    }

    async function send() {
        // 返信するメッセージ欄が未入力か判定する。
        if (utils.isNullEmpty(contactMessage.value)) {
            messageDialog('error', i18n.global.t('contact.message.message1'));
            return;
        }

        // 返信するメッセージの文字数が超過しているか判定する。
        if (utils.checkLengthOfLongString(contactMessage.value)) {
            messageDialog('error', i18n.global.t('contact.message.message2'));
            return;
        }
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();
        // 登録ボタン表示制御
        disabledControl.value = true;

        const requestBody = {
            ContactMsg: contactMessage.value,
        };

        // Storeクラスを経由して連絡メッセージ登録APIをコールする。
        const response = await contactStore.postContactDetail(API_URL.value, requestBody);
        if (response.success) {
            const contactMsgPk = response.responseBody.ContactMsgPk;

            if (selectedUploadFile.value.length > 0) {
                for (let i = 0; i < selectedUploadFile.value.length; i++) {
                    const data = selectedUploadFile.value[i];

                    const requestBody2 = {
                        ContactMsgPk: contactMsgPk,
                        ContactMsgFileNm: data.name,
                    };

                    // Storeクラスを経由して連絡メッセージ添付ファイルアップロードAPIをコールする。
                    const response2 = await contactStore.postContactFileUpload(API_URL.value, requestBody2);
                    if (response2.success) {
                        const queryParam3 = {
                            UldHistPk: response2.responseBody.UldHistPk,
                        };

                        // アップロード履歴取得APIをコールする。
                        const response3 = await fileUploadStore.getUploadFileHistoryPkFromApi(API_URL.value, queryParam3);
                        if (response3.success) {
                            const fileUploadResults = await fileUploadDownloadService.fileUpload(response3.responseBody.UldS3Url, data.data);

                            if (fileUploadResults.status === 200) {
                                const requestBody3 = {
                                    UldHistPk: response2.responseBody.UldHistPk,
                                    UldStatus: '2',
                                };
                                // アップロード履歴更新APIをコールする。
                                const response4 = await fileUploadStore.updateUploadFileHistoryPkFromApi(API_URL.value, requestBody3);
                                if (!response4.success) {
                                    messageDialog('error', response4.errorMessage);
                                    if (response4.redirect) {
                                        location.href = response4.redirectUrl;
                                    }
                                }
                            }
                        } else {
                            messageDialog('error', response3.errorMessage);
                            if (response3.redirect) {
                                location.href = response3.redirectUrl;
                            }
                        }
                    } else {
                        messageDialog('error', response2.errorMessage);
                        if (response2.redirect) {
                            location.href = response2.redirectUrl;
                        }
                    }
                }
            }

            await reset();
            await postContactListInfo();
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
        // 登録ボタン表示制御
        disabledControl.value = false;
    }

    async function fileDownload(DldHistPk, FileNm) {
        const userAgent = window.navigator.userAgent.toLowerCase();

        // ダウンロード処理はiOSのみ動作が微妙に異なるためiOSかどうか判定する。
        if (userAgent.indexOf('iphone') !== -1 || userAgent.indexOf('ipad') !== -1) {
            // iOSの場合。

            // 連絡添付ファイルダウンロードAPIのリクエストボディを生成する。
            const requestBody = {
                ContactMsgFilePk: DldHistPk, // 連絡メッセージファイルPK
            };

            // 連絡添付ファイルダウンロードAPIをコールする。
            const response = await contactStore.postContactFileDownload(API_URL.value, requestBody);
            if (response.success) {
                const DldHistPk = response.responseBody.DldHistPk;
                if (!utils.isNullEmpty(DldHistPk)) {
                    // ダウンロード履歴取得APIのクエリパラメーターを生成する。
                    const queryParamDldHistGet = {
                        DldHistPk: DldHistPk, //ダウンロード履歴PK
                    };

                    // 念のためタイマーイベントを全てクリアする。
                    // 万が一実行中のタイマーイベントが残っている場合は強制停止する。
                    clearTimeout();

                    // タイマーで監視し、APIのエラー時のループを制御する。
                    let isDownloadTimeoutError = false;

                    // ダウンロード処理開始後からダウンロード処理が完了せずに15分経過した場合は、エラーとしてダウンロード処理を停止する。
                    const downloadTotalErrorTimer = 900000;

                    // ダウンロード履歴取得APIをコールする間隔のタイマー値を設定する。
                    // ダウンロード可能かどうかのチェック処理後、2秒間のインターバルを設けて、再度APIをコールする。
                    const callDownloadHistoryApiintervalTimer = 2000;

                    // ダウンロード対象のファイル名を宣言する。
                    const fileName = FileNm;

                    // ダウンロード処理の総経過時間を監視し、15分経過した場合に呼び出されるファンクションを宣言する。
                    const checkTimeout = function () {
                        // ダウンロード総経過時間監視フラグを有効にして、ダウンロード限界時間経過としてエラー発生扱いにする。
                        isDownloadTimeoutError = true;

                        // 念のためタイマーイベントを全てクリアする。
                        // 万が一実行中のタイマーイベントが残っている場合は強制停止する。
                        clearTimeout();
                    };

                    // ダウンロード処理構造体を宣言する。
                    // ダウンロード履歴取得API、ダウンロード履歴更新APIの実行タイミングを制御し、ダウンロード可能なファイルが存在する場合はJavascriptでダウンロードを実行する。
                    const downloadEventPromise = async function (id) {
                        await new Promise(function (resolve, reject) {
                            // ダウンロード履歴取得APIから、ダウンロード対象ファイルの状態を取得し、本処理で判定して制御する。

                            // もし、ダウンロード総経過時間フラグが有効の場合は、以降の処理を実行せず、ダウンロード限界時間経過としてエラーにして、rejectで返却する。
                            if (isDownloadTimeoutError) {
                                reject('download timeout error');
                                return;
                            }

                            // ダウンロード履歴取得APIの判定したダウンロードファイルの状態に応じて制御する。
                            // ファイルダウンロード処理Javascript側で状況を判定している。
                            switch (id) {
                                case 'continue':
                                case 'startDownload':
                                case 'nowDownloading':
                                case 'endDownload':
                                    // ダウンロード準備中。
                                    // ダウンロード可能状態の応答が返ってきた。
                                    // Javascriptでファイルをダウンロードして、正常に完了した状態。
                                    resolve(id);
                                    break;
                                case 'error':
                                case 'break':
                                default:
                                    // ダウンロード履歴取得APIでダウンロードエラーが発生。
                                    // ダウンロード履歴取得APIからダウンロード完了が返却された場合。
                                    // 上記以外の本処理内での例外の場合。
                                    // ダウンロード処理を停止するためにrejectで返却する。
                                    reject(id);
                                    break;
                            }
                        })
                            .then(async function (resolved) {
                                // Promiseでresolveの正常応答の場合。

                                // もし、ダウンロード総経過時間フラグが有効の場合は、以降の処理を実行しない。
                                if (isDownloadTimeoutError) {
                                    return;
                                }

                                // ダウンロード履歴取得APIから取得したステータスと、ダウンロード処理スクリプトで判定した結果に応じて制御する。
                                switch (resolved) {
                                    case 'startDownload':
                                        // iOSの場合は、ダウンロード履歴更新APIを実行するまでに少しだけタイムラグを持たせる。
                                        // iOSでのJavascriptのダウンロード処理が終わるだろうタイミングの余裕を持たせる。
                                        setTimeout(async function () {
                                            await downloadEventPromise('nowDownloading');
                                        }, 5000);
                                        break;
                                    case 'nowDownloading':
                                        {
                                            // ダウンロード履歴取得APIからダウンロード可能ステータスが返却されたので、Javascriptでファイルダウンロードを実行した場合。

                                            // ダウンロード履歴更新APIのリクエストボディを生成する。
                                            const requestBodyDldHistPut = {
                                                DldHistPk: DldHistPk,
                                                DldStatus: '3', // ダウンロード中モードに設定する。
                                            };

                                            // ダウンロード履歴更新APIをコールする。
                                            const responseDldHistPut = await fileDownloadStore.updateDownloadFileHistoryPkFromApi(API_URL.value, requestBodyDldHistPut);

                                            // APIの応答結果に応じて制御する。
                                            if (responseDldHistPut.success) {
                                                // APIの正常応答を検知して、再度本ダウンロードPromiseを実行する。
                                                await downloadEventPromise('continue');
                                            } else {
                                                // エラーの場合。
                                                messageDialog('error', responseDldHistPut.errorMessage);
                                                if (responseDldHistPut.redirect) {
                                                    location.href = responseDldHistPut.redirectUrl;
                                                }
                                            }
                                        }
                                        break;
                                    case 'endDownload':
                                        {
                                            // Javascriptでファイルダウンロードを実行した場合。

                                            // ダウンロード履歴更新APIのリクエストボディを生成する。
                                            const requestBodyDldHistPut = {
                                                DldHistPk: DldHistPk,
                                                DldStatus: '4', // ダウンロード完了に設定する。
                                            };

                                            // ダウンロード履歴更新APIをコールする。
                                            const responseDldHistPut = await fileDownloadStore.updateDownloadFileHistoryPkFromApi(API_URL.value, requestBodyDldHistPut);

                                            // APIの応答結果に応じて制御する。
                                            if (responseDldHistPut.success) {
                                                // APIの正常応答を検知して、再度本ダウンロードPromiseを実行する。
                                                await downloadEventPromise('continue');
                                            } else {
                                                // エラーの場合。
                                                messageDialog('error', responseDldHistPut.errorMessage);
                                                if (responseDldHistPut.redirect) {
                                                    location.href = responseDldHistPut.redirectUrl;
                                                }
                                            }
                                        }
                                        break;
                                    case 'continue':
                                        // ダウンロード履歴取得APIを指定のインターバルの時間経過後に再度コールするタイマーイベント。
                                        setTimeout(async function () {
                                            // ダウンロード履歴取得APIをコールする。
                                            const responseDldHistGet2 = await fileDownloadStore.getDownloadFileHistoryPkFromApi(API_URL.value, queryParamDldHistGet);

                                            // APIの応答結果に応じて制御する。
                                            if (responseDldHistGet2.success) {
                                                // ファイルダウンロードスクリプトを実行する。
                                                // ダウンロード履歴取得APIから取得したダウンロード状態、ダウンロードファイルの座標、ダウンロード対象のファイル名を引数に設定する。
                                                const fileUploadDownloadServiceResult2 = await fileUploadDownloadService.fileDownload(responseDldHistGet2.responseBody.DldStatus, responseDldHistGet2.responseBody.DldS3Url, fileName);

                                                // ファイルダウンロードスクリプト実行後、再度本ダウンロードPromiseを実行する。
                                                await downloadEventPromise(fileUploadDownloadServiceResult2);
                                            } else {
                                                // ダウンロード履歴取得APIがエラーの場合。
                                                messageDialog('error', responseDldHistGet2.errorMessage);
                                                if (responseDldHistGet2.redirect) {
                                                    location.href = responseDldHistGet2.redirectUrl;
                                                }
                                            }
                                        }, callDownloadHistoryApiintervalTimer);
                                        break;
                                    default:
                                        // 何もしない
                                        break;
                                }
                            })
                            .catch(function (/*rejected*/) {
                                // Promiseでrejectのルートで処理された場合。

                                // ダウンロードが完了した、や、エラーが発生したので、ダウンロード総経過時間フラグを更新して、以降の処理を実行しないようにする。
                                isDownloadTimeoutError = true;

                                // 念のためタイマーイベントを全てクリアする。
                                // 万が一実行中のタイマーイベントが残っている場合は強制停止する。
                                clearTimeout();
                            });
                    };

                    // ***** ダウンロードの初期処理を実行する - ここから - **** //

                    // ダウンロード総時間を監視する。
                    setTimeout(checkTimeout, downloadTotalErrorTimer);

                    // 初回のダウンロード履歴取得APIをコールする。
                    const responseDldHistGet = await fileDownloadStore.getDownloadFileHistoryPkFromApi(API_URL.value, queryParamDldHistGet);

                    // APIの応答結果に応じて制御する。
                    if (responseDldHistGet.success) {
                        // ファイルダウンロードスクリプトを実行する。
                        // ダウンロード履歴取得APIから取得したダウンロード状態、ダウンロードファイルの座標、ダウンロード対象のファイル名を引数に設定する。
                        const fileUploadDownloadServiceResult = await fileUploadDownloadService.fileDownload(responseDldHistGet.responseBody.DldStatus, responseDldHistGet.responseBody.DldS3Url, fileName);

                        // ファイルダウンロードスクリプト実行後、ファイルダウンロード処理Promiseを実行する。
                        await downloadEventPromise(fileUploadDownloadServiceResult);
                    } else {
                        // ダウンロード履歴取得APIがエラーの場合。
                        messageDialog('error', responseDldHistGet.errorMessage);
                        if (responseDldHistGet.redirect) {
                            location.href = responseDldHistGet.redirectUrl;
                        }
                    }

                    // ***** ダウンロードの初期処理を実行する - ここまで - **** //
                }
            } else {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
            }
        } else {
            // iOS以外の場合。
            // WindowsのEdgeやChrome、マッキントッシュ、Android端末の場合。

            // 連絡添付ファイルダウンロードAPIのリクエストボディを生成する。
            const requestBody = {
                ContactMsgFilePk: DldHistPk, // 連絡メッセージファイルPK
            };

            // 連絡添付ファイルダウンロードAPIをコールする。
            const response = await contactStore.postContactFileDownload(API_URL.value, requestBody);
            if (response.success) {
                const DldHistPk = response.responseBody.DldHistPk;
                if (!utils.isNullEmpty(DldHistPk)) {
                    // ダウンロード履歴取得APIのクエリパラメーターを生成する。
                    const queryParamDldHistGet = {
                        DldHistPk: DldHistPk, //ダウンロード履歴PK
                    };

                    // 念のためタイマーイベントを全てクリアする。
                    // 万が一実行中のタイマーイベントが残っている場合は強制停止する。
                    clearTimeout();

                    // タイマーで監視し、APIのエラー時のループを制御する。
                    let isDownloadTimeoutError = false;

                    // ダウンロード処理開始後からダウンロード処理が完了せずに15分経過した場合は、エラーとしてダウンロード処理を停止する。
                    const downloadTotalErrorTimer = 900000;

                    // ダウンロード履歴取得APIをコールする間隔のタイマー値を設定する。
                    // ダウンロード可能かどうかのチェック処理後、2秒間のインターバルを設けて、再度APIをコールする。
                    const callDownloadHistoryApiintervalTimer = 2000;

                    // ダウンロード対象のファイル名を宣言する。
                    const fileName = FileNm;

                    // ダウンロード処理の総経過時間を監視し、15分経過した場合に呼び出されるファンクションを宣言する。
                    const checkTimeout = function () {
                        // ダウンロード総経過時間監視フラグを有効にして、ダウンロード限界時間経過としてエラー発生扱いにする。
                        isDownloadTimeoutError = true;

                        // 念のためタイマーイベントを全てクリアする。
                        // 万が一実行中のタイマーイベントが残っている場合は強制停止する。
                        clearTimeout();
                    };

                    // ダウンロード処理構造体を宣言する。
                    // ダウンロード履歴取得API、ダウンロード履歴更新APIの実行タイミングを制御し、ダウンロード可能なファイルが存在する場合はJavascriptでダウンロードを実行する。
                    const downloadEventPromise = async function (id) {
                        await new Promise(function (resolve, reject) {
                            // ダウンロード履歴取得APIから、ダウンロード対象ファイルの状態を取得し、本処理で判定して制御する。

                            // もし、ダウンロード総経過時間フラグが有効の場合は、以降の処理を実行せず、ダウンロード限界時間経過としてエラーにして、rejectで返却する。
                            if (isDownloadTimeoutError) {
                                reject('download timeout error');
                                return;
                            }

                            // ダウンロード履歴取得APIの判定したダウンロードファイルの状態に応じて制御する。
                            // ファイルダウンロード処理Javascript側で状況を判定している。
                            switch (id) {
                                case 'continue':
                                case 'startDownload':
                                case 'endDownload':
                                    // ダウンロード準備中。
                                    // ダウンロード可能状態の応答が返ってきた。
                                    // Javascriptでファイルをダウンロードして、正常に完了した状態。
                                    resolve(id);
                                    break;
                                case 'error':
                                case 'break':
                                default:
                                    // ダウンロード履歴取得APIでダウンロードエラーが発生。
                                    // ダウンロード履歴取得APIからダウンロード完了が返却された場合。
                                    // 上記以外の本処理内での例外の場合。
                                    // ダウンロード処理を停止するためにrejectで返却する。
                                    reject(id);
                                    break;
                            }
                        })
                            .then(async function (resolved) {
                                // Promiseでresolveの正常応答の場合。

                                // もし、ダウンロード総経過時間フラグが有効の場合は、以降の処理を実行しない。
                                if (isDownloadTimeoutError) {
                                    return;
                                }

                                // ダウンロード履歴取得APIから取得したステータスと、ダウンロード処理スクリプトで判定した結果に応じて制御する。
                                switch (resolved) {
                                    case 'startDownload':
                                        {
                                            // ダウンロード履歴取得APIからダウンロード可能ステータスが返却されたので、Javascriptでファイルダウンロードを実行した場合。

                                            // ダウンロード履歴更新APIのリクエストボディを生成する。
                                            const requestBodyDldHistPut = {
                                                DldHistPk: DldHistPk,
                                                DldStatus: '3', // ダウンロード中モードに設定する。
                                            };

                                            // ダウンロード履歴更新APIをコールする。
                                            const responseDldHistPut = await fileDownloadStore.updateDownloadFileHistoryPkFromApi(API_URL.value, requestBodyDldHistPut);

                                            // APIの応答結果に応じて制御する。
                                            if (responseDldHistPut.success) {
                                                // APIの正常応答を検知して、再度本ダウンロードPromiseを実行する。
                                                await downloadEventPromise('continue');
                                            } else {
                                                // エラーの場合。
                                                messageDialog('error', responseDldHistPut.errorMessage);
                                                if (responseDldHistPut.redirect) {
                                                    location.href = responseDldHistPut.redirectUrl;
                                                }
                                            }
                                        }
                                        break;
                                    case 'endDownload':
                                        {
                                            // Javascriptでファイルダウンロードを実行した場合。

                                            // ダウンロード履歴更新APIのリクエストボディを生成する。
                                            const requestBodyDldHistPut = {
                                                DldHistPk: DldHistPk,
                                                DldStatus: '4', // ダウンロード完了に設定する。
                                            };

                                            // ダウンロード履歴更新APIをコールする。
                                            const responseDldHistPut = await fileDownloadStore.updateDownloadFileHistoryPkFromApi(API_URL.value, requestBodyDldHistPut);

                                            // APIの応答結果に応じて制御する。
                                            if (responseDldHistPut.success) {
                                                // APIの正常応答を検知して、再度本ダウンロードPromiseを実行する。
                                                await downloadEventPromise('continue');
                                            } else {
                                                // エラーの場合。
                                                messageDialog('error', responseDldHistPut.errorMessage);
                                                if (responseDldHistPut.redirect) {
                                                    location.href = responseDldHistPut.redirectUrl;
                                                }
                                            }
                                        }
                                        break;
                                    case 'continue':
                                        // ダウンロード履歴取得APIを指定のインターバルの時間経過後に再度コールするタイマーイベント。
                                        setTimeout(async function () {
                                            // ダウンロード履歴取得APIをコールする。
                                            const responseDldHistGet2 = await fileDownloadStore.getDownloadFileHistoryPkFromApi(API_URL.value, queryParamDldHistGet);

                                            // APIの応答結果に応じて制御する。
                                            if (responseDldHistGet2.success) {
                                                // ファイルダウンロードスクリプトを実行する。
                                                // ダウンロード履歴取得APIから取得したダウンロード状態、ダウンロードファイルの座標、ダウンロード対象のファイル名を引数に設定する。
                                                const fileUploadDownloadServiceResult2 = await fileUploadDownloadService.fileDownload(responseDldHistGet2.responseBody.DldStatus, responseDldHistGet2.responseBody.DldS3Url, fileName);

                                                // ファイルダウンロードスクリプト実行後、再度本ダウンロードPromiseを実行する。
                                                await downloadEventPromise(fileUploadDownloadServiceResult2);
                                            } else {
                                                // ダウンロード履歴取得APIがエラーの場合。
                                                messageDialog('error', responseDldHistGet2.errorMessage);
                                                if (responseDldHistGet2.redirect) {
                                                    location.href = responseDldHistGet2.redirectUrl;
                                                }
                                            }
                                        }, callDownloadHistoryApiintervalTimer);
                                        break;
                                    default:
                                        // 何もしない
                                        break;
                                }
                            })
                            .catch(function (/*rejected*/) {
                                // Promiseでrejectのルートで処理された場合。

                                // ダウンロードが完了した、や、エラーが発生したので、ダウンロード総経過時間フラグを更新して、以降の処理を実行しないようにする。
                                isDownloadTimeoutError = true;

                                // 念のためタイマーイベントを全てクリアする。
                                // 万が一実行中のタイマーイベントが残っている場合は強制停止する。
                                clearTimeout();
                            });
                    };

                    // ***** ダウンロードの初期処理を実行する - ここから - **** //

                    // ダウンロード総時間を監視する。
                    setTimeout(checkTimeout, downloadTotalErrorTimer);

                    // 初回のダウンロード履歴取得APIをコールする。
                    const responseDldHistGet = await fileDownloadStore.getDownloadFileHistoryPkFromApi(API_URL.value, queryParamDldHistGet);

                    // APIの応答結果に応じて制御する。
                    if (responseDldHistGet.success) {
                        // ファイルダウンロードスクリプトを実行する。
                        // ダウンロード履歴取得APIから取得したダウンロード状態、ダウンロードファイルの座標、ダウンロード対象のファイル名を引数に設定する。
                        const fileUploadDownloadServiceResult = await fileUploadDownloadService.fileDownload(responseDldHistGet.responseBody.DldStatus, responseDldHistGet.responseBody.DldS3Url, fileName);

                        // ファイルダウンロードスクリプト実行後、ファイルダウンロード処理Promiseを実行する。
                        await downloadEventPromise(fileUploadDownloadServiceResult);
                    } else {
                        // ダウンロード履歴取得APIがエラーの場合。
                        messageDialog('error', responseDldHistGet.errorMessage);
                        if (responseDldHistGet.redirect) {
                            location.href = responseDldHistGet.redirectUrl;
                        }
                    }

                    // ***** ダウンロードの初期処理を実行する - ここまで - **** //
                }
            } else {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
            }
        }
    }

    async function reset() {
        selectedUploadFile.value = [];
        contactMessage.value = '';
    }
</script>

<style></style>

<template>
    <article id="modalContactToCounselor" class="modal" :style="{ display: contactDispFlg }">
        <h3>{{ $t('contact.parts.modalTitle1') }}</h3>
        <section class="modalContents">
            <ul class="contactHistoryBlock">
                <li v-for="(data, index) in contactListBody" :key="'constant' + index" :class="{ counselor: data.ContactMsgType === '2' }">
                    <ul>
                        <li>
                            <template v-if="data.ContactMsgType === '1'">
                                <!--連絡窓口-->
                                <span>{{ $t('contact.parts.dl2') }}</span>
                            </template>
                            <template v-else>
                                <!--あなた-->
                                <span>{{ $t('contact.parts.dl1') }}</span>
                            </template>
                        </li>
                        <li>
                            <!--送信日時-->
                            <span>{{ data.ContactMsgSendDt }}</span>
                        </li>
                    </ul>
                    <div class="message">
                        <template v-if="data.ContactDeleted">
                            <!--このメッセージは削除されました-->
                            <p>{{ $t('common.parts.messageDeleted') }}</p>
                        </template>
                        <template v-else>
                            <!--連絡メッセージ-->
                            <p>{{ data.ContactMsg }}</p>
                        </template>
                        <template v-if="data.ContactMsgFiles.length > 0">
                            <!--添付ファイル-->
                            <ul class="contactHistoryBlockBtnArea">
                                <li v-for="(data2, index2) in data.ContactMsgFiles" :key="'downloadFile' + index + '_' + index2">
                                    <button type="button" class="links" @click="fileDownload(data2.ContactMsgFilePk, data2.ContactMsgFileNm)">{{ data2.ContactMsgFileNm }}</button>
                                </li>
                            </ul>
                        </template>
                    </div>
                </li>
            </ul>
            <hr class="line" />
            <div id="newMessage">
                <ul class="formInput">
                    <li>
                        <div id="textArea">
                            <textarea v-model="contactMessage" />
                            <ul class="contactHistoryBlockBtnArea">
                                <template v-for="(data, index) in selectedUploadFile" :key="'uploadFile' + index">
                                    <li>
                                        <span>{{ data.name }}</span>
                                    </li>
                                    <li>
                                        <button type="button" class="delete" @click="removeUploadTargetFile(index)">&nbsp;</button>
                                    </li>
                                </template>
                                <li>
                                    <label class="upload"><input type="file" id="file" @change="fileSelect($event)" />{{ $t('contact.parts.modalButton2') }}</label>
                                </li>
                            </ul>
                        </div>
                    </li>
                </ul>
                <ul class="btnArea right">
                    <li>
                        <button type="button" class="btnPrimary" @click="send" :disabled="disabledControl || disabledStore.isDisabled">{{ $t('contact.parts.modalButton3') }}</button>
                    </li>
                </ul>
            </div>
        </section>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <button type="button" class="btnNormal" @click="cancel">{{ $t('common.parts.close') }}</button>
                </li>
            </ul>
        </section>
    </article>
    <!--連絡-->
</template>
